import React from "react";
import Select, { type GroupBase, type SingleValue } from "react-select";
import type { StylesConfig } from "react-select/dist/declarations/src/styles.d";

import { zindexModal } from "~/css/zindexes";
import reactSelect from "~/ui/react-select";

export type OptionType = {
	label: string;
	value: string;
};

export function mapSelectorItemsToReactSelect(list: readonly { name: string; externalId: string }[]): OptionType[] {
	return list.map(project => ({
		label: project.name,
		value: project.externalId,
	}));
}

export function useChangeListener(setter: (id: string) => void, nameSetter?: (name: string) => void) {
	return React.useCallback(
		(value: SingleValue<OptionType>) => {
			setter(value?.value || "");
			if (nameSetter) {
				nameSetter(value?.label || "");
			}
		},
		[setter, nameSetter],
	);
}

export function ItemSelect({
	options,
	selectedId,
	setSelectedId,
	setSelectedName,
	placeholder,
	isClearable = false,
	isLoading,
	isDisabled,
	isError,
	shouldAssumeDefault,
	selectFirstItem,
}: {
	options: OptionType[];
	selectedId?: string;
	setSelectedId: (id: string) => void;
	setSelectedName?: (name: string) => void;
	placeholder: string;
	isClearable?: boolean;
	isLoading: boolean;
	isDisabled?: boolean;
	isError?: boolean;
	shouldAssumeDefault?: boolean; // Will default to the ONLY option, if there is 1 option only
	selectFirstItem?: boolean; // Will default to the FIRST option, if there are multiple options
}) {
	const onChangeListener = useChangeListener(setSelectedId, setSelectedName);
	const selectedOption = options.find(option => option.value === selectedId);

	React.useEffect(() => {
		if (((shouldAssumeDefault && options.length === 1) || (selectFirstItem && options.length > 1)) && !selectedOption) {
			setSelectedId(options[0].value);
			if (setSelectedName) {
				setSelectedName(options[0].label);
			}
		}
	}, [shouldAssumeDefault, setSelectedId, setSelectedName, selectedId, selectedOption, options, selectFirstItem]);

	return (
		<Select<OptionType, false>
			styles={selectStyles(isError)}
			components={selectComponents}
			menuPortalTarget={document.body}
			menuPosition="fixed"
			placeholder={placeholder}
			isClearable={isClearable || false}
			isLoading={isLoading}
			isDisabled={isDisabled}
			value={selectedOption || null}
			options={options}
			onChange={onChangeListener}
			{...reactSelect}
		/>
	);
}

const ellipses = {
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	width: "100%",
	overflow: "hidden",
} as const;

const errorStyles = {
	borderColor: "var(--font-red) !important",
	background: "var(--veryMutedRed) !important",
};

function selectStyles(isError?: boolean): StylesConfig<OptionType, false, GroupBase<OptionType>> {
	return {
		menuPortal: base => ({ ...base, zIndex: zindexModal + 1 }),
		option: base => ({
			...base,
			...ellipses,
		}),
		placeholder: base => ({
			...base,
			...ellipses,
		}),
		...(isError
			? {
					control: base => ({
						...base,
						...errorStyles,
					}),
				}
			: {}),
	};
}

export const selectComponents = {
	IndicatorSeparator: () => null,
};
